import { create } from "zustand";
import { createJSONStorage, persist, StateStorage } from "zustand/middleware";
import { OpusAgentModel } from "@src/types.generated";
import { isOpusAgentModelAvailable } from "./models";

// The admin's last explicitly picked Opus Agent model, persisted per the
// localStorage rule (zustand + persist, in-memory state as the truth). It
// seeds the selection for NEW chats only — an existing chat's stored model
// (server truth) always wins. Device preference, not signed-in-user data,
// so logout.tsx deliberately does not clear it. A model no longer offered
// in the picker is no preference at all, so it never seeds a new chat.
export const sanitizeStoredModel = (value: unknown): OpusAgentModel | null =>
  typeof value === "string" &&
  (Object.values(OpusAgentModel) as string[]).includes(value) &&
  isOpusAgentModelAvailable(value as OpusAgentModel)
    ? (value as OpusAgentModel)
    : null;

type ModelPreferenceState = {
  preferredModel: OpusAgentModel | null;
  setPreferredModel: (model: OpusAgentModel) => void;
};

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

const safeLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      return;
    }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      return;
    }
  },
};

export const useOpusAgentModelPreferenceStore = create<ModelPreferenceState>()(
  persist(
    (set) => ({
      preferredModel: null,
      setPreferredModel: (model) =>
        set({ preferredModel: sanitizeStoredModel(model) }),
    }),
    {
      name: "opus-agent-model-preference",
      version: 1,
      storage: createJSONStorage(() =>
        typeof window === "undefined" ? noopStorage : safeLocalStorage,
      ),
      partialize: (state) => ({ preferredModel: state.preferredModel }),
      merge: (persisted, current) => ({
        ...current,
        // An invalid or since-removed enum value falls back to no preference.
        preferredModel: sanitizeStoredModel(
          (persisted as { preferredModel?: unknown } | null)?.preferredModel,
        ),
      }),
    },
  ),
);
